import React, { useEffect, useState, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link } from "react-router-dom";
import { getGoogleLanguages, getLanguages } from "../lib/api";
import LanguageDrawer from "./AddLanugage";

const API_BASE = process.env.REACT_APP_API_BASE || "https://api.soksan-dss.com";

const LanguageLayer = () => {
  // table data (from your DB)
  const [languages, setLanguages] = useState([]);
  // google supported languages for dropdowns
  const [googleLanguages, setGoogleLanguages] = useState([]);
  // loading states
  const [loadingTable, setLoadingTable] = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  
  // Drawer states
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  
  // pagination / UI extras
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);

  // fetch table languages from DB
  const fetchLanguages = async () => {
    try {
      setLoadingTable(true);
      const data = await getLanguages();
      if (data.success) {
        setLanguages(data.data || []);
      } else {
        console.error("Failed to fetch languages:", data.message);
      }
    } catch (err) {
      console.error("Error fetching languages:", err);
    } finally {
      setLoadingTable(false);
    }
  };

  const fetchGoogleLanguages = async () => {
    try {
      setLoadingGoogle(true);
      const data = await getGoogleLanguages();

      if (data.languages && Array.isArray(data.languages)) {
        const langs = data.languages.map((lang) => ({
          code: lang.language || lang.code || lang.languageCode,
          name: lang.name || lang.displayName || lang.language
        }));
        setGoogleLanguages(langs);
      } else if (Array.isArray(data)) {
        const langs = data.map((lang) => ({
          code: lang.language || lang.code || lang.languageCode,
          name: lang.name || lang.displayName || lang.language
        }));
        setGoogleLanguages(langs);
      } else {
        console.error("Unexpected response format for Google languages:", data);
        setGoogleLanguages([]);
      }
    } catch (err) {
      console.error("Error fetching google languages:", err);
      setGoogleLanguages([]);
    } finally {
      setLoadingGoogle(false);
    }
  };

  useEffect(() => {
    fetchLanguages();
    fetchGoogleLanguages();
  }, []);

  // open edit drawer and prefill
  const openEditDrawer = (item) => {
    setSelectedLanguage(item);
    setShowEditDrawer(true);
  };

  // handle success from drawer
  const handleDrawerSuccess = () => {
    fetchLanguages(); // Refresh the table data
  };

  // small helper to render image (thumbnail)
  const renderImage = (imgName) => {
    if (!imgName) return null;
    return <img src={`${API_BASE}/uploads/lang_sym/${imgName}`} alt="lang" width="36" className="rounded" />;
  };

  // pagination slice
  const paged = languages.slice((page - 1) * perPage, page * perPage);
  const pageCount = Math.ceil(languages.length / perPage) || 1;

  return (
    <>
      <div className="card h-100 p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <span className="text-md fw-medium text-secondary-light mb-0">Show</span>
            <select
              className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(1);
              }}
            >
              {[5, 10, 20, 50].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>

            <form
              className="navbar-search"
              onSubmit={(ev) => {
                ev.preventDefault();
                // optional: add search backend or client filter
                alert("Implement search endpoint if needed");
              }}
            >
              <input type="text" className="bg-base h-40-px w-auto" name="search" placeholder="Search" />
              <Icon icon="ion:search-outline" className="icon" />
            </form>
          </div>

          <button
            type="button"
            className="btn btn-yellow text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
            onClick={() => setShowAddDrawer(true)}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
            Add Languages
          </button>
        </div>

        <div className="card h-100 p-16 radius-12">
          <div className="table-responsive scroll-sm">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th scope="col" className="text-center">Name</th>
                  <th scope="col" className="text-center">Code</th>
                  <th scope="col" className="text-center">Google Code</th>
                  <th scope="col" className="text-center">Image</th>
                  <th scope="col" className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {loadingTable ? (
                  <tr><td colSpan="6" className="text-center">Loading languages...</td></tr>
                ) : paged.length ? (
                  paged.map((item, idx) => (
                    <tr key={item.ID}>
                      <td className="text-center">{item.LanguageName}</td>
                      <td className="text-center">{item.LanguageCode}</td>
                      <td className="text-center">{item.GoogleLanguageCode}</td>
                      <td className="text-center">{renderImage(item.ImageOfLanguageSymbol)}</td>
                      <td className="text-center">
                        <div className="d-flex align-items-center gap-10 justify-content-center">
                          <button
                            type="button"
                            className="bg-success-100 text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            onClick={() => openEditDrawer(item)}
                          >
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </button>
                          {/* <button
                            type="button"
                            className="remove-item-button bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            // onClick={() => handleDelete(item.ID)}
                          >
                            <Icon icon="fluent:delete-24-regular" className="menu-icon" />
                          </button> */}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="6" className="text-center">No languages found</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
            <span>
              Showing {(page - 1) * perPage + 1} to {Math.min(page * perPage, languages.length)} of {languages.length} entries
            </span>
            <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
              <li className="page-item">
                <Link
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md"
                  to="#"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <Icon icon="ep:d-arrow-left" />
                </Link>
              </li>
              {Array.from({ length: pageCount }).map((_, i) => (
                <li key={i} className="page-item">
                  <Link
                    className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${page === i + 1 ? "bg-primary-600 text-white" : "bg-neutral-200"}`}
                    to="#"
                    onClick={() => setPage(i + 1)}
                  >
                    {i + 1}
                  </Link>
                </li>
              ))}
              <li className="page-item">
                <Link
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md"
                  to="#"
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                >
                  <Icon icon="ep:d-arrow-right" />
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Add Language Drawer */}
      <LanguageDrawer
        isOpen={showAddDrawer}
        onClose={() => setShowAddDrawer(false)}
        googleLanguages={googleLanguages}
        onSuccess={handleDrawerSuccess}
      />

      {/* Edit Language Drawer */}
      <LanguageDrawer
        isOpen={showEditDrawer}
        onClose={() => {
          setShowEditDrawer(false);
          setSelectedLanguage(null);
        }}
        languageToEdit={selectedLanguage}
        googleLanguages={googleLanguages}
        onSuccess={handleDrawerSuccess}
      />
    </>
  );
};

export default LanguageLayer;